import { useQuery } from '@tanstack/react-query'
import { AppliedPtoItem, PromoCodeItem, SearchAppliedPtosParams } from 'Modules'

import { AppAdmin } from 'api/app-admin'
import { mapApiToAppliedPto } from 'modules/promo-code/api-adapters'
import { logError } from 'utils/logger'

import { promoCodeKeys } from './query-keys'

interface AppliedPtosResult {
  results: AppliedPtoItem[]
  total: number
}

const fetchAppliedPtos = async (
  promoCodeId: string,
  params: SearchAppliedPtosParams
): Promise<AppliedPtosResult> => {
  try {
    const res = await AppAdmin.getPromoCodeAppliedPtos(promoCodeId, {
      status: params.status ? params.status.toLowerCase() : undefined,
      page: params.page,
      limit: params.limit,
    })
    const payload = res.data?.data
    const results = (payload?.results ?? []).map(mapApiToAppliedPto)
    return { results, total: payload?.pagination?.totalResults ?? results.length }
  } catch (error) {
    logError('Error fetching applied PTOs:', error)
    return { results: [], total: 0 }
  }
}

/**
 * PTOs that applied a given promo code — GET /admin/platform-promo-codes/:id/ptos
 * (the "Total PTOs Applied" popup).
 */
export const useAppliedPtos = (
  promoCode: PromoCodeItem | null,
  params: SearchAppliedPtosParams
) => {
  const query = useQuery({
    queryKey: promoCodeKeys.appliedPtos(promoCode?.id, params),
    queryFn: () => fetchAppliedPtos(promoCode!.id, params),
    enabled: !!promoCode,
    gcTime: 30000,
  })

  return {
    ptos: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
  }
}
